import { Enum }				from "@v3/data/enum";
import { Textual }			from "@v3/data/textual";
import { Numeric }			from "@v3/data/numeric";
import {
	FiniteNumber,
	NaturalNumber,
	NonEmptyString,
	EmailAddress,
}	from "@v3/data/primitive";

import { CountryId }			from "@geotoura/shared/i18n";

export type DestinationId	= Numeric.Value<typeof DestinationId>;
export const DestinationId	= NaturalNumber.rebrand("DestinationId");

export type HotelId			= Numeric.Value<typeof HotelId>;
export const HotelId		= NaturalNumber.rebrand("HotelId");

export type RegionId		= Numeric.Value<typeof RegionId>;
export const RegionId		= NaturalNumber.rebrand("RegionId");

export type TerritoryId		= Numeric.Value<typeof TerritoryId>;
export const TerritoryId	= NaturalNumber.rebrand("TerritoryId");

export type ExampleRouteId	= Numeric.Value<typeof ExampleRouteId>;
export const ExampleRouteId	= NaturalNumber.rebrand("ExampleRouteId");

export type AccessCode		= Textual.Value<typeof AccessCode>;
export const AccessCode		= NonEmptyString.rebrand("AccessCode");

export type CarCategoryId	= Numeric.Value<typeof CarCategoryId>;
export const CarCategoryId	= NaturalNumber.rebrand("CarCategoryId");

export type CarCategory = Readonly<{
	id:		CarCategoryId,
	name:	string,
}>;

export type HotelCategoryId		= Numeric.Value<typeof HotelCategoryId>;
export const HotelCategoryId	= NaturalNumber.rebrand("HotelCategoryId");

export type HotelCategory = Readonly<{
	id:		HotelCategoryId,
	name:	string,
}>;

export type HotelSiteId		= Numeric.Value<typeof HotelSiteId>;
export const HotelSiteId	= NaturalNumber.rebrand("HotelSiteId");

export type HotelSite = Readonly<{
	id:		HotelSiteId,
	name:	string,
}>;

export type IsoCountry	= Readonly<{
	code:	CountryId,
	name:	string,
}>;

export type RouteName = Readonly<{
	name:	string,
}>;

export const MapAssetFileType	= Enum.of("driving", "static");
export type MapAssetFileType	= Enum.Value<typeof MapAssetFileType>;

//-----------------------------------------------------------------------------

// we send lots of these in the up api, therefore the property names have to be short
export type Coordinate	= Readonly<{
	lat:	Latitude,
	lng:	Longitude,
}>;

export type Latitude		= Numeric.Value<typeof Latitude>;
export const Latitude		= FiniteNumber.rebrand("Latitude");

export type Longitude		= Numeric.Value<typeof Longitude>;
export const Longitude		= FiniteNumber.rebrand("Longitude");

export type SecondDuration	= Numeric.Value<typeof SecondDuration>;
export const SecondDuration	= FiniteNumber.rebrand("SecondDuration");

export type MeterDistance	= Numeric.Value<typeof MeterDistance>;
export const MeterDistance	= FiniteNumber.rebrand("MeterDistance");

//-----------------------------------------------------------------------------

export type HtmlSnippet		= Textual.Value<typeof HtmlSnippet>;
export const HtmlSnippet	= Textual.branded("HtmlSnippet");

export const Gender	= Enum.of("m", "w", "d");
export type Gender	= Enum.Value<typeof Gender>;

export type FirstName	= Textual.Value<typeof FirstName>;
export const FirstName	=
	Textual.branded("FirstName")
	.test(it => it.length <= 50, "must have a maximum of 50 characters");

export type LastName	= Textual.Value<typeof LastName>;
export const LastName	=
	Textual.branded("LastName")
	.test(it => it.length <= 50, "must have a maximum of 50 characters");

export type Title	= Textual.Value<typeof Title>;
export const Title	=
	Textual.branded("Title")
	.test(it => it.length <= 10, "must have a maximum of 10 characters");

export type EmailAddressLtd		= Textual.Value<typeof EmailAddressLtd>;
export const EmailAddressLtd	=
	EmailAddress.rebrand("EmailAddressLtd")
	.test(it => it.length <= 50, "must have a maximum of 50 characters");

export type Telephone	= Textual.Value<typeof Telephone>;
export const Telephone	=
	Textual.branded("Telephone")
	.test(it => it.length <= 50, "must have a maximum of 50 characters");

export type PostCode	= Textual.Value<typeof PostCode>;
export const PostCode	=
	Textual.branded("PostCode")
	.test(it => it.length <= 16, "must have a maximum of 16 characters");

export type ParameterValue	= Textual.Value<typeof ParameterValue>;
export const ParameterValue	=
	Textual.branded("ParameterValue")
	.test(it => it.length <= 1000, "must have a maximum of 1000 characters");
